/*
 * Copyright (c) 2020 NOOGA DEVELOPMENT, All rights reserved.
 * Author Raman Kremko <raman.kremko@noogadev.com>
 */

import { Epic, ofType } from 'redux-observable';
import { catchError, exhaustMap, map, mapTo, switchMap, takeUntil, tap } from 'rxjs/operators';
import { from, of } from 'rxjs';
import { alertsClient } from '../api/alerts/alertsClient';
import { RootState } from '../store.typings';
import {
    AlertsDetailsAction,
    fetchAlertItemsSuccessAction,
    AlertsDetailsActionType,
    alertsDetailsErrorAction,
    updateAlertItemSuccessAction,
    fetchAlertDetailsSuccessAction,
    reportLoadFinishAction,
    updateAlertDetailsSuccess,
} from '../actions/alertDetailsActions';
import { downloadDataAsFile, parseError } from '../../utils/utils';
import { AxiosError } from 'axios';
import { i18n } from '../translation/i18n';

type AlertsDetailsEpic = Epic<AlertsDetailsAction, AlertsDetailsAction, RootState>;

export const fetchAlertDetails: AlertsDetailsEpic = (action$, state$) => {
    return action$.pipe(
        ofType(AlertsDetailsActionType.OPEN_ALERTS_DETAILS_PAGE),
        switchMap(() => {
            const alertId = state$.value.routerState.route.params.AlertId;

            return from(alertsClient.getAlertDetails(alertId)).pipe(
                map((details) => fetchAlertDetailsSuccessAction(details)),

                catchError((err: AxiosError) =>
                    of(
                        alertsDetailsErrorAction(
                            parseError(err, i18n.t('AlertsScreens.errors.failedLoadAlertDetails')),
                        ),
                    ),
                ),

                takeUntil(action$.pipe(ofType(AlertsDetailsActionType.EXIT_ALERTS_DETAILS_PAGE))),
            );
        }),
    );
};

export const fetchAlertItemsEpic: AlertsDetailsEpic = (action$, state$) => {
    return action$.pipe(
        ofType(AlertsDetailsActionType.OPEN_ALERTS_DETAILS_PAGE, AlertsDetailsActionType.CHANGE_ALERT_ITEMS_PAGE),
        switchMap(() => {
            const alertId = state$.value.routerState.route.params.AlertId;
            const params = {
                limit: state$.value.alertsDetailsReducer.pageSize,
                offset: state$.value.alertsDetailsReducer.page,
            };

            return from(alertsClient.getAlertItems(alertId, params)).pipe(
                map((items) => fetchAlertItemsSuccessAction(items)),

                catchError((err: AxiosError) =>
                    of(
                        alertsDetailsErrorAction(
                            parseError(err, i18n.t('AlertsScreens.errors.failedLoadAlertDetails')),
                        ),
                    ),
                ),

                takeUntil(action$.pipe(ofType(AlertsDetailsActionType.EXIT_ALERTS_DETAILS_PAGE))),
            );
        }),
    );
};

export const updateAlertItemEpic: AlertsDetailsEpic = (action$) => {
    return action$.pipe(
        ofType(AlertsDetailsActionType.UPDATE_ALERT_ITEM_REQ),
        switchMap((action) => {
            return from(alertsClient.updateAlertItem(action.payload)).pipe(
                map((respData) => updateAlertItemSuccessAction(respData)),

                catchError((err: AxiosError) =>
                    of(alertsDetailsErrorAction(parseError(err, i18n.t('errors.failedTryAgain')))),
                ),
            );
        }),
    );
};

export const updateAlertDetailsEpic: AlertsDetailsEpic = (action$) => {
    return action$.pipe(
        ofType(AlertsDetailsActionType.UPDATE_ALERT_DETAILS),
        switchMap((action) => {
            return from(alertsClient.updateAlerts(action.payload)).pipe(
                map((updatedAlertArr) => updateAlertDetailsSuccess(updatedAlertArr)),

                catchError((err: AxiosError) =>
                    of(alertsDetailsErrorAction(parseError(err, i18n.t('errors.failedTryAgain')))),
                ),
            );
        }),
    );
};

export const reportLoadEpic: AlertsDetailsEpic = (actions$) => {
    return actions$.pipe(
        ofType(AlertsDetailsActionType.REPORT_LOAD_START),

        exhaustMap((action) => {
            const fileName = `alert_${action.payload}_Report.xlsx`;

            return from(alertsClient.loadAlertDataReportFile(action.payload)).pipe(
                tap((fileData) => downloadDataAsFile(fileName, fileData)),
                mapTo(reportLoadFinishAction()),

                catchError((err: AxiosError) =>
                    of(alertsDetailsErrorAction(parseError(err, i18n.t('errors.failedUploadFile')))),
                ),
            );
        }),
    );
};
